import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { orderBy } from 'lodash';
import { FaFolderOpen, FaFolder, FaImage, FaFileAlt, FaVideo, FaTrash, FaCloudUploadAlt, FaFilePdf, FaPlay } from 'react-icons/fa';

// UI Components - Design System V4.0
import {
  DrawerContainer,
  DrawerContent,
  MultimediaNavigation,
  Banner,
  BannerContent,
  BannerTitle,
  BannerDescription,
  BannerImage,
  gradients,
  SoftButton,
  SearchInput,
} from '../../../components/ui';

// Character image for banner
import multimediaCharacter from '../../../assets/characters-banners/multimedia.png';

import {
  MediaGrid,
  MediaCard,
  MediaCardThumbnail,
  MediaCardIcon,
  MediaCardInfo,
  MediaCardTitle,
  MediaCardMeta,
  MediaCardMetaRow,
  MediaToolbar,
  MediaToolbarLeft,
  MediaToolbarRight,
  OrderByContainer,
  MediaEmpty,
  MediaEmptyIcon,
  MediaEmptyText,
} from './Multimedia.style';

import { getLabelMessage, getJcnError } from '../../../const/labelService';
import GeneralModal from '../../../components/shared/components/generalModal/GeneralModal.component';
import { getAlbumsAction, createAlbumAction, setAlbumAsSelectedAction } from '../../../redux/multimediaDuck';
import formatDateUtil from '../../../tools/date-utils';
import LoaderComponent from '../../../components/loader/Loader.component';
import toastConfig from '../../../components/shared/constants/toaster-config';
import OrderByModal from '../../../components/orderByModal/OrderByModal.component';
import CONSTANTS from '../../../const/constants';

// Banner configuration per section
const sectionConfig = {
  all: {
    gradient: gradients.pink,
    icon: FaFolderOpen,
    title: 'Galería Multimedia',
    description: 'Sube y organiza PDFs, imágenes y catálogos para enviarlos rápidamente en tus conversaciones de WhatsApp.',
  },
  images: {
    gradient: gradients.blue,
    icon: FaImage,
    title: 'Imágenes',
    description: 'Visualiza y gestiona todas las imágenes de tu galería. Formatos soportados: JPG, PNG, GIF.',
  },
  documents: {
    gradient: gradients.orange,
    icon: FaFileAlt,
    title: 'Documentos',
    description: 'Accede a todos tus documentos PDF almacenados en la galería.',
  },
  videos: {
    gradient: gradients.purple,
    icon: FaVideo,
    title: 'Videos',
    description: 'Gestiona todos tus archivos de video. Formato soportado: MP4.',
  },
  trash: {
    gradient: gradients.gray,
    icon: FaTrash,
    title: 'Papelera',
    description: 'Archivos eliminados recientemente. Los archivos en la papelera se eliminan automáticamente después de 30 días.',
  },
};

// Thumbnail color configurations based on content type
const thumbnailColors = {
  folder: { bg: '#FEF3C7', icon: '#D97706' },      // Yellow/Amber for folders
  image: { bg: '#FCE7F3', icon: '#DB2777' },       // Pink for images
  pdf: { bg: '#FECACA', icon: '#DC2626' },         // Red for PDFs
  video: { bg: '#D1FAE5', icon: '#059669' },       // Green for videos
  document: { bg: '#DBEAFE', icon: '#2563EB' },    // Blue for documents
};

const Multimedia = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const labels = [
    'multimediaOrderByLabel',
    'multimediaCreationDate',
    'multimediaEmpty',
    'multimediaAddNewModalAlbum',
    'multimediaCreateAlbum',
    'btnCancel',
    'createNewAlbumButtonLabel',
  ];
  const lbls = getLabelMessage(labels);

  const albums = useSelector((state) => state.multimedia.albums);
  const isLoadingAlbums = useSelector((state) => state.multimedia.fetching);
  const [displayedAlbums, setDisplayedAlbums] = useState([...albums]);
  const [searchValue, setSearchValue] = useState('');
  const canWrite = useSelector((state) => state.user?.data?.permissions?.writeMultimedia);

  const [isCreateNewAlbumModalOpen, setIsCreateNewAlbumModalOpen] = useState(false);
  const [isNewAlbumLoading, setIsNewAlbumLoading] = useState(false);
  const [activeView, setActiveView] = useState('all');

  const selectedSpaceName = useSelector((state) => state.spaces.selected.name);
  const selectedSpaceId = useSelector((state) => state.spaces.selected.id);
  const arrayForm = [{ name: 'createNewAlbumName', show: true }];

  useEffect(() => {
    dispatch(getAlbumsAction([selectedSpaceId]));
  }, []);

  useEffect(() => {
    dispatch(getAlbumsAction([selectedSpaceId]));
  }, [selectedSpaceName]);

  useEffect(() => {
    setDisplayedAlbums(albums);
  }, [albums]);

  const handleSearch = (e) => {
    const newDisplayedAlbums = albums.filter((x) => x.name.toLowerCase().includes(e.target.value.toLowerCase()));
    setDisplayedAlbums(newDisplayedAlbums);
    setSearchValue(e.target.value);
  };

  const onAlbumClick = (albumId) => {
    dispatch(setAlbumAsSelectedAction(albumId));
    history.push({
      pathname: CONSTANTS.ALBUM_ROUTE,
      state: { filterType: activeView !== 'all' ? activeView : null },
    });
  };

  const createAlbum = async (data) => {
    try {
      setIsNewAlbumLoading(true);
      await dispatch(createAlbumAction(data.createNewAlbumName, data.createNewAlbumSpaceSelect));
      history.push(CONSTANTS.ALBUM_ROUTE);
    } catch (error) {
      toast.error(getJcnError(error.message), toastConfig);
    } finally {
      setIsNewAlbumLoading(false);
    }
  };

  const orderByOption = (option) => {
    if (option === 'creationDate') {
      const newAlbumOrder = orderBy(displayedAlbums, [(o) => moment(o.createdAt).format('x')]);
      setDisplayedAlbums(newAlbumOrder);
    } else if (option === 'name') {
      const newAlbumOrder = orderBy(displayedAlbums, [(o) => o.name.toLowerCase()]);
      setDisplayedAlbums(newAlbumOrder);
    }
  };

  const handleViewChange = (viewId) => {
    setActiveView(viewId);
  };

  const currentSection = sectionConfig[activeView] || sectionConfig.all;

  // Filter albums based on view and file type
  const getFilteredAlbums = () => {
    if (activeView === 'trash') {
      return []; // Trash is empty for now
    }

    if (activeView === 'all') {
      return displayedAlbums;
    }

    // Filter albums that contain files of the selected type
    const fileTypeMap = {
      images: CONSTANTS.FILE_TYPE_IMAGE,
      documents: CONSTANTS.FILE_TYPE_PDF,
      videos: CONSTANTS.FILE_TYPE_VIDEO,
    };

    const targetFileType = fileTypeMap[activeView];
    if (!targetFileType) return displayedAlbums;

    // Return albums that have at least one file of the target type
    return displayedAlbums.filter((album) =>
      album.files?.some((file) => file.fileType === targetFileType)
    );
  };

  // Get file count for the current view
  const getFileCountForView = (album) => {
    if (activeView === 'all') {
      return album.files?.length || 0;
    }

    const fileTypeMap = {
      images: CONSTANTS.FILE_TYPE_IMAGE,
      documents: CONSTANTS.FILE_TYPE_PDF,
      videos: CONSTANTS.FILE_TYPE_VIDEO,
    };

    const targetFileType = fileTypeMap[activeView];
    if (!targetFileType) return album.files?.length || 0;

    return album.files?.filter((file) => file.fileType === targetFileType).length || 0;
  };

  const filteredAlbums = getFilteredAlbums();

  const renderContent = () => {
    if (isLoadingAlbums) {
      return <LoaderComponent width={70} height={70} />;
    }

    return (
      <>
        <Banner gradient={currentSection.gradient}>
          <BannerContent>
            <BannerTitle>{currentSection.title}</BannerTitle>
            <BannerDescription>{currentSection.description}</BannerDescription>
            {canWrite && activeView === 'all' && (
              <SoftButton
                onClick={() => setIsCreateNewAlbumModalOpen(true)}
                style={{ marginTop: '16px', background: 'white' }}
              >
                <FaCloudUploadAlt style={{ marginRight: '8px' }} />
                Subir Archivo
              </SoftButton>
            )}
          </BannerContent>
          <BannerImage src={multimediaCharacter} alt="Multimedia" />
        </Banner>

        <div style={{ marginTop: '24px' }}>
          <MediaToolbar>
            <MediaToolbarLeft>
              <SearchInput
                placeholder="Buscar archivo o carpeta..."
                value={searchValue}
                onChange={handleSearch}
                style={{ minWidth: '200px', maxWidth: '300px' }}
              />
            </MediaToolbarLeft>
            <MediaToolbarRight>
              <OrderByContainer>
                <span>Ordenar por</span>
                <OrderByModal onOptionClick={orderByOption} />
              </OrderByContainer>
            </MediaToolbarRight>
          </MediaToolbar>

          {activeView === 'trash' ? (
            <MediaEmpty>
              <MediaEmptyIcon><FaTrash /></MediaEmptyIcon>
              <MediaEmptyText>La papelera está vacía</MediaEmptyText>
            </MediaEmpty>
          ) : filteredAlbums?.length === 0 ? (
            <MediaEmpty>
              <MediaEmptyIcon>
                {activeView === 'images' ? <FaImage /> :
                 activeView === 'documents' ? <FaFilePdf /> :
                 activeView === 'videos' ? <FaVideo /> : <FaFolderOpen />}
              </MediaEmptyIcon>
              <MediaEmptyText>
                {activeView === 'images' ? 'No hay álbumes con imágenes' :
                 activeView === 'documents' ? 'No hay álbumes con documentos' :
                 activeView === 'videos' ? 'No hay álbumes con videos' :
                 lbls.multimediaEmpty}
              </MediaEmptyText>
            </MediaEmpty>
          ) : (
            <MediaGrid>
              {filteredAlbums?.map((album) => {
                // Get thumbnail based on current view and file types
                const getThumbnail = () => {
                  // Check for images first (they have actual previews)
                  if (activeView === 'images' || activeView === 'all') {
                    const imageFile = album.files?.find((x) => x.fileType === CONSTANTS.FILE_TYPE_IMAGE);
                    if (imageFile?.url) return { type: 'image', url: imageFile.url };
                  }

                  // For documents view
                  if (activeView === 'documents') {
                    const docFile = album.files?.find((x) => x.fileType === CONSTANTS.FILE_TYPE_PDF);
                    if (docFile) return { type: 'icon', Icon: FaFilePdf, colors: thumbnailColors.pdf };
                  }

                  // For videos view
                  if (activeView === 'videos') {
                    const videoFile = album.files?.find((x) => x.fileType === CONSTANTS.FILE_TYPE_VIDEO);
                    if (videoFile) return { type: 'icon', Icon: FaPlay, colors: thumbnailColors.video };
                  }

                  // Default: try to find any image first
                  const anyImage = album.files?.find((x) => x.fileType === CONSTANTS.FILE_TYPE_IMAGE);
                  if (anyImage?.url) return { type: 'image', url: anyImage.url };

                  // Folder icon as default
                  return { type: 'icon', Icon: FaFolder, colors: thumbnailColors.folder };
                };

                const thumbnail = getThumbnail();
                const fileCount = getFileCountForView(album);

                // Get file type label and icon
                const getFileMeta = () => {
                  if (activeView === 'images') {
                    return {
                      label: `${fileCount} imagen${fileCount !== 1 ? 'es' : ''}`,
                      Icon: FaImage,
                      color: thumbnailColors.image.icon
                    };
                  }
                  if (activeView === 'documents') {
                    return {
                      label: `${fileCount} documento${fileCount !== 1 ? 's' : ''}`,
                      Icon: FaFilePdf,
                      color: thumbnailColors.pdf.icon
                    };
                  }
                  if (activeView === 'videos') {
                    return {
                      label: `${fileCount} video${fileCount !== 1 ? 's' : ''}`,
                      Icon: FaVideo,
                      color: thumbnailColors.video.icon
                    };
                  }
                  return {
                    label: `${fileCount} archivo${fileCount !== 1 ? 's' : ''}`,
                    Icon: FaFolder,
                    color: thumbnailColors.folder.icon
                  };
                };

                const fileMeta = getFileMeta();
                const MetaIcon = fileMeta.Icon;

                return (
                  <MediaCard key={album.id} onClick={() => onAlbumClick(album.id)}>
                    <MediaCardThumbnail bgColor={thumbnail.colors?.bg}>
                      {thumbnail.type === 'image' ? (
                        <img src={thumbnail.url} alt={album.name} />
                      ) : (
                        <MediaCardIcon iconColor={thumbnail.colors?.icon}>
                          <thumbnail.Icon />
                        </MediaCardIcon>
                      )}
                    </MediaCardThumbnail>
                    <MediaCardInfo>
                      <MediaCardTitle>{album.name}</MediaCardTitle>
                      <MediaCardMetaRow>
                        <MediaCardMeta iconColor={fileMeta.color}>
                          <MetaIcon />
                          {fileMeta.label}
                        </MediaCardMeta>
                        <MediaCardMeta>
                          {formatDateUtil(album.createdAt, '/')}
                        </MediaCardMeta>
                      </MediaCardMetaRow>
                    </MediaCardInfo>
                  </MediaCard>
                );
              })}
            </MediaGrid>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <DrawerContainer>
        <MultimediaNavigation
          activeView={activeView}
          onViewChange={handleViewChange}
        />
        <DrawerContent>
          {renderContent()}
        </DrawerContent>
      </DrawerContainer>

      <GeneralModal
        type="general"
        mainText={lbls.multimediaAddNewModalAlbum}
        confirmText={lbls.multimediaCreateAlbum}
        cancelText={lbls.btnCancel}
        handleCancelButton={() => setIsCreateNewAlbumModalOpen(false)}
        handleConfirmButton={(data) => createAlbum(data)}
        show={isCreateNewAlbumModalOpen}
        arrayForm={arrayForm}
        loadingForm={isNewAlbumLoading}
      />
    </>
  );
};

export default Multimedia;
